import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";
import { saveOrendaAuth, clearOrendaAuth } from "./orendaAuthStorage";

// Cognito quirk: the cognito-idp issuer's OpenID discovery document publishes
// JWKS + userinfo but NOT authorization_endpoint / token_endpoint /
// end_session_endpoint (those live under the hosted-UI domain). We fetch the
// discovery doc for JWKS/issuer, then override the endpoints so sign-in /
// sign-out actually work.
const HOSTED_UI = "https://bopayments.auth.eu-west-1.amazoncognito.com";

// Singleton UserManager for the Cognito flow. Import this anywhere in the app
// that needs to trigger sign-in, read the current user, or sign out.
export const orendaUserManager = new UserManager({
  authority: orendaOidcConfig.authority,
  client_id: orendaOidcConfig.client_id,
  redirect_uri: orendaOidcConfig.redirect_uri,
  post_logout_redirect_uri: orendaOidcConfig.post_logout_redirect_uri,
  response_type: orendaOidcConfig.response_type,
  scope: orendaOidcConfig.scope,
  metadataUrl: orendaOidcConfig.metadataUrl,
  metadataSeed: {
    authorization_endpoint: `${HOSTED_UI}/oauth2/authorize`,
    token_endpoint: `${HOSTED_UI}/oauth2/token`,
    userinfo_endpoint: `${HOSTED_UI}/oauth2/userInfo`,
    end_session_endpoint: `${HOSTED_UI}/logout`,
    revocation_endpoint: `${HOSTED_UI}/oauth2/revoke`,
  },
  // Popup flow closes this page after the callback fires.
  popup_redirect_uri: orendaOidcConfig.redirect_uri,
  popup_post_logout_redirect_uri: orendaOidcConfig.post_logout_redirect_uri,
  // Cache tokens in sessionStorage (matches existing Card-Console pattern).
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: orendaOidcConfig.automaticSilentRenew,
  // Fire the accessTokenExpiring event 60s before expiry so we can silently
  // renew (default is 60s; being explicit for clarity).
  accessTokenExpiringNotificationTimeInSeconds: 60,
});

// Silent renewal glue.
// When oidc-client-ts refreshes the token, `addUserLoaded` fires with the new
// User object. We mirror the new tokens into our sessionStorage.orendaAuth
// entry so every service call (which reads via readOrendaAuth) sees the fresh
// value. If refresh fails (refresh_token expired, network, etc.) we clear the
// session and let the landing page kick off a fresh signinRedirect.
orendaUserManager.events.addUserLoaded((user) => {
  saveOrendaAuth({
    id_token: user.id_token,
    access_token: user.access_token,
    refresh_token: user.refresh_token,
    expires_in: user.expires_in,
    token_type: user.token_type,
    scope: user.scope,
  });
});

orendaUserManager.events.addSilentRenewError((err) => {
  // Do not clear here — let the landing page's session-status check drive the
  // "sign in again" UI. Just log so we can see it in the console.
  // eslint-disable-next-line no-console
  console.warn("[orenda] silent renew failed:", err?.message || err);
});

orendaUserManager.events.addUserSignedOut(() => {
  clearOrendaAuth();
});

// Hard logout: hits Cognito's hosted-UI /logout endpoint directly, which
// clears the *Cognito* session cookie (the piece MSAL/oidc-client can't touch
// because it lives on the cognito domain). Without this, Cognito silently
// reuses the last-signed-in account on next /oauth2/authorize call —
// prompt=login doesn't reliably override it for federated IdPs.
//
// Flow: clear local storage → navigate to Cognito /logout → Cognito clears
// its cookie → redirects to logout_uri (our /orenda/logout page) → user is
// back on our site with a clean slate.
//
// We flip isSigningOut() to true for the duration of the navigation so other
// components (OrendaLanding, service calls) can suppress error UI that would
// otherwise flash — e.g. an in-flight viewapplication racing with the logout
// returns 401 and would render a "Something went wrong" screen before the
// browser navigates. Checking this flag lets the UI stay quiet instead.
let _isSigningOut = false;
export const isSigningOut = () => _isSigningOut;

export const hardLogoutOrenda = () => {
  _isSigningOut = true;
  try {
    window.sessionStorage.removeItem("orendaAuth");
    window.sessionStorage.removeItem("orendaCard");
    // Clear oidc-client-ts's own storage too
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith("oidc.")) window.sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
  const logoutUrl = new URL(`${HOSTED_UI}/logout`);
  logoutUrl.searchParams.set("client_id", orendaOidcConfig.client_id);
  logoutUrl.searchParams.set(
    "logout_uri",
    orendaOidcConfig.post_logout_redirect_uri
  );
  window.location.replace(logoutUrl.toString());
};
